// src/pages/services/OEMReturns.js
// OEM Return Tracking System
// SSOT: service_tasks.defective_items[] (source of truth for parts)
//       oem_returns collection (lifecycle tracking)
//
// Flow:
// 1. SM sees all defective items from closed services
// 2. Creates OEM return doc (groups items from one/multiple services)
// 3. Tracks status: pending → dispatched → received_by_oem → replaced/credited
// 4. Updates oem_return_status on each defective_item in service_task

import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  collection, getDocs, addDoc, updateDoc,
  doc, query, where, serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase";

const getUser = () => JSON.parse(localStorage.getItem("user") || "{}");

const STATUS_CONFIG = {
  pending:         { label: "Pending",        bg: "#fef9c3", color: "#854d0e", dot: "#eab308" },
  dispatched:      { label: "Dispatched",     bg: "#dbeafe", color: "#1e40af", dot: "#3b82f6" },
  received_by_oem: { label: "Received by OEM",bg: "#ede9fe", color: "#5b21b6", dot: "#8b5cf6" },
  replaced:        { label: "Replaced ✓",     bg: "#dcfce7", color: "#166534", dot: "#22c55e" },
  credited:        { label: "Credited",       bg: "#d1fae5", color: "#065f46", dot: "#10b981" },
};

const OEM_STATUSES = ["pending", "dispatched", "received_by_oem", "replaced", "credited"];

export default function OEMReturns() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const me        = getUser();
  const companyId = me.company_id || "";

  // ── List mode ──────────────────────────────────────────────────────────
  const [oemReturns,   setOemReturns]   = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");

  // ── Create mode ────────────────────────────────────────────────────────
  const [showCreate,   setShowCreate]   = useState(false);
  const [services,     setServices]     = useState([]); // closed services with defective items
  const [loadingServices, setLoadingServices] = useState(false);
  const [selectedParts, setSelectedParts] = useState([]); // { service_id, item_index, item }
  const [oem_name,     setOemName]      = useState("");
  const [saving,       setSaving]       = useState(false);

  // ── Status update ──────────────────────────────────────────────────────
  const [updatingId,   setUpdatingId]   = useState("");

  useEffect(() => {
  fetchOemReturns();
  // eslint-disable-line react-hooks/exhaustive-deps
}, [filterStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Deep-link from ServiceDetail: auto-open create modal + preselect ────
  useEffect(() => {
    const preselectServiceId = location.state?.preselect_service_id;
    if (preselectServiceId) {
      openCreate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  useEffect(() => {
    const preselectServiceId = location.state?.preselect_service_id;
    if (!preselectServiceId || !services.length) return;
    const targetService = services.find(s => s.id === preselectServiceId);
    if (!targetService) return;
    const pendingParts = (targetService.defective_items || [])
      .map((item, idx) => ({ item, idx }))
      .filter(({ item }) => !item.oem_return_status || item.oem_return_status === "pending")
      .map(({ item, idx }) => ({ key: `${preselectServiceId}_${idx}`, service_id: preselectServiceId, item_index: idx, item }));
    if (pendingParts.length) setSelectedParts(pendingParts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services]);

  const fetchOemReturns = async () => {
    setLoading(true);
    try {
      const q = filterStatus === "all"
        ? query(collection(db, "oem_returns"), where("company_id", "==", companyId))
        : query(collection(db, "oem_returns"), where("company_id", "==", companyId), where("status", "==", filterStatus));
      const snap = await getDocs(q);
      const list = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.created_at?.seconds ?? 0) - (a.created_at?.seconds ?? 0));
      setOemReturns(list);
    } catch (err) {
      console.error("fetchOemReturns error:", err);
    } finally {
      setLoading(false);
    }
  };

  // ── Load closed services with pending defective items ─────────────────
  const fetchServicesWithDefectives = async () => {
    setLoadingServices(true);
    try {
      const snap = await getDocs(query(
        collection(db, "service_tasks"),
        where("company_id", "==", companyId),
      ));
      const withDefectives = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(s =>
          s.defective_items?.some(item => !item.oem_return_status || item.oem_return_status === "pending")
        )
        .sort((a, b) => (b.created_at?.seconds ?? 0) - (a.created_at?.seconds ?? 0));
      setServices(withDefectives);
    } catch (err) {
      console.error("fetchServicesWithDefectives error:", err);
    } finally {
      setLoadingServices(false);
    }
  };

  const openCreate = () => {
    setShowCreate(true);
    setSelectedParts([]);
    setOemName("");
    fetchServicesWithDefectives();
  };

  // ── Toggle part selection ──────────────────────────────────────────────
  const togglePart = (serviceId, itemIndex, item) => {
    const key = `${serviceId}_${itemIndex}`;
    const exists = selectedParts.find(p => p.key === key);
    if (exists) {
      setSelectedParts(prev => prev.filter(p => p.key !== key));
    } else {
      setSelectedParts(prev => [...prev, { key, service_id: serviceId, item_index: itemIndex, item }]);
    }
  };

  // ── Create OEM return doc ──────────────────────────────────────────────
  const handleCreate = async () => {
    if (!selectedParts.length) { alert("Select at least one defective part"); return; }
    setSaving(true);
    try {
      // Generate OEM return number
      const countSnap = await getDocs(query(
        collection(db, "oem_returns"),
        where("company_id", "==", companyId)
      ));
      const oemNo = `OEM-${String(countSnap.size + 1).padStart(4, "0")}`;

      // Build items array
      const items = selectedParts.map(p => ({
        service_id:          p.service_id,
        service_complaint:   services.find(s => s.id === p.service_id)?.complaint || "",
        client_name:         services.find(s => s.id === p.service_id)?.client_name || "",
        site_name:           services.find(s => s.id === p.service_id)?.site_name || "",
        item_index:          p.item_index,
        item_name:           p.item.item_name,
        qty:                 p.item.qty || 1,
        machine_description: p.item.machine_description || "",
        machine_model:       p.item.machine_model       || "",
        serial_no:           p.item.serial_no           || "",
        part_name:           p.item.part_name           || "",
        part_code:           p.item.part_code           || "",
        remarks:             p.item.remarks             || "",
        added_at:            p.item.added_at            || "",
      }));

      // Create OEM return doc
      await addDoc(collection(db, "oem_returns"), {
        company_id:   companyId,
        oem_return_no: oemNo,
        oem_name:     oem_name.trim(),
        status:       "pending",
        items,
        service_ids:  [...new Set(selectedParts.map(p => p.service_id))],
        created_by:   me.uid || me.id || "",
        created_by_name: me.name || "",
        created_at:   serverTimestamp(),
        updated_at:   serverTimestamp(),
      });

      // Update oem_return_status on each defective_item in service_task
      for (const p of selectedParts) {
        const service = services.find(s => s.id === p.service_id);
        if (!service) continue;
        const updatedDefectives = (service.defective_items || []).map((item, idx) =>
          idx === p.item_index
            ? { ...item, oem_return_status: "returned", oem_return_no: oemNo }
            : item
        );
        await updateDoc(doc(db, "service_tasks", p.service_id), {
          defective_items: updatedDefectives,
          updated_at: serverTimestamp(),
        });
      }

      alert(`✅ OEM Return ${oemNo} created`);
      setShowCreate(false);
      fetchOemReturns();
    } catch (err) {
      console.error("handleCreate error:", err);
      alert(err.message || "Failed to create OEM return");
    } finally {
      setSaving(false);
    }
  };

  // ── Update status ──────────────────────────────────────────────────────
  const handleStatusUpdate = async (oemReturn, newStatus) => {
    if (updatingId) return;
    setUpdatingId(oemReturn.id);
    try {
      await updateDoc(doc(db, "oem_returns", oemReturn.id), {
        status:     newStatus,
        updated_at: serverTimestamp(),
        [`${newStatus}_at`]: serverTimestamp(),
        [`${newStatus}_by`]: me.name || me.uid || "",
      });

      // If replaced/credited → update service_task items
      if (newStatus === "replaced" || newStatus === "credited") {
        for (const item of oemReturn.items || []) {
          const serviceSnap = await getDocs(query(
            collection(db, "service_tasks"),
            where("company_id", "==", companyId)
          ));
          const serviceDoc = serviceSnap.docs.find(d => d.id === item.service_id);
          if (!serviceDoc) continue;
          const updatedDefectives = (serviceDoc.data().defective_items || []).map((di, idx) =>
            idx === item.item_index
              ? { ...di, oem_return_status: newStatus }
              : di
          );
          await updateDoc(doc(db, "service_tasks", item.service_id), {
            defective_items: updatedDefectives,
            updated_at: serverTimestamp(),
          });
        }
      }

      fetchOemReturns();
    } catch (err) {
      console.error("handleStatusUpdate error:", err);
      alert(err.message || "Status update failed");
    } finally {
      setUpdatingId("");
    }
  };

  // ── RENDER ─────────────────────────────────────────────────────────────
  return (
    <div className="p-4 pb-20 bg-gray-50 min-h-screen max-w-3xl mx-auto">
      <button onClick={() => navigate(-1)}
        className="text-sm text-slate-500 hover:text-slate-800 mb-4 block">
        ← Back
      </button>

      <div className="flex justify-between items-center mb-5">
        <div>
          <h1 className="text-xl font-bold text-slate-800">OEM Returns</h1>
          <p className="text-xs text-slate-400 mt-0.5">Track defective parts sent back to manufacturer</p>
        </div>
        <button onClick={openCreate}
          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-xl text-sm font-semibold transition">
          + New OEM Return
        </button>
      </div>

      {/* FILTER */}
      <div className="flex gap-2 flex-wrap mb-4">
        {["all", ...OEM_STATUSES].map(s => (
          <button key={s} onClick={() => setFilterStatus(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition capitalize ${
              filterStatus === s
                ? "bg-slate-800 text-white border-slate-800"
                : "bg-white text-slate-600 border-slate-200"
            }`}>
            {s === "all" ? "All" : STATUS_CONFIG[s]?.label || s}
          </button>
        ))}
      </div>

      {/* LIST */}
      {loading ? (
        <p className="text-slate-400 text-sm text-center mt-10">Loading...</p>
      ) : oemReturns.length === 0 ? (
        <div className="text-center mt-16">
          <div className="text-4xl mb-3">📦</div>
          <p className="text-slate-500">No OEM returns found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {oemReturns.map(oem => {
            const ui = STATUS_CONFIG[oem.status] || STATUS_CONFIG.pending;
            const nextStatuses = OEM_STATUSES.filter(s => OEM_STATUSES.indexOf(s) > OEM_STATUSES.indexOf(oem.status));
            return (
              <div key={oem.id}
                className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4"
                style={{ borderLeft: `4px solid ${ui.dot}` }}>

                {/* Header */}
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="font-bold text-slate-800">{oem.oem_return_no}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {oem.oem_name || "OEM not specified"} •{" "}
                      {oem.created_by_name} •{" "}
                      {oem.created_at?.seconds
                        ? new Date(oem.created_at.seconds * 1000).toLocaleDateString("en-IN")
                        : "—"}
                    </p>
                  </div>
                  <span className="text-xs font-bold px-3 py-1 rounded-full"
                    style={{ background: ui.bg, color: ui.color }}>
                    {ui.label}
                  </span>
                </div>

                {/* Items */}
                <div className="space-y-2 mb-3">
                  {(oem.items || []).map((item, i) => (
                    <div key={i} className="bg-slate-50 rounded-xl p-3 text-xs">
                      <div className="flex justify-between items-start">
                        <p className="font-semibold text-slate-700">{item.item_name}</p>
                        <span className="text-slate-400">Qty: {item.qty}</span>
                      </div>
                      <p className="text-slate-500 mt-0.5">
                        🔧 {item.machine_description || "—"}
                        {item.machine_model && ` • ${item.machine_model}`}
                      </p>
                      <p className="text-slate-500">
                        📋 S/N: <span className="font-mono font-semibold">{item.serial_no || "—"}</span>
                        {item.part_code && <span className="ml-2">🏷 {item.part_code}</span>}
                      </p>
                      <p className="text-slate-400 mt-0.5">
                        📍 {item.client_name} → {item.site_name}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Status actions */}
                {nextStatuses.length > 0 && (
                  <div className="flex gap-2 flex-wrap">
                    {nextStatuses.slice(0, 2).map(ns => (
                      <button
                        key={ns}
                        onClick={() => handleStatusUpdate(oem, ns)}
                        disabled={!!updatingId}
                        className="px-3 py-1.5 rounded-xl text-xs font-semibold border transition disabled:opacity-40"
                        style={{
                          background: STATUS_CONFIG[ns]?.bg,
                          color: STATUS_CONFIG[ns]?.color,
                          borderColor: STATUS_CONFIG[ns]?.dot,
                        }}>
                        {updatingId === oem.id ? "Updating..." : `→ Mark as ${STATUS_CONFIG[ns]?.label}`}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ════ CREATE MODAL ════ */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] flex flex-col">

            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="font-bold text-slate-800">New OEM Return</h2>
              <button onClick={() => setShowCreate(false)}
                className="text-slate-400 hover:text-slate-700 text-xl">✕</button>
            </div>

            <div className="p-5 overflow-y-auto flex-1 space-y-4">

              {/* OEM Name */}
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">
                  OEM / Manufacturer Name
                </label>
                <input
                  placeholder="e.g. Daikin India, Blue Star, Voltas"
                  value={oem_name}
                  onChange={e => setOemName(e.target.value)}
                  className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-red-300"
                />
              </div>

              {/* Select parts */}
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase mb-2 block">
                  Select Defective Parts to Return
                </label>
                {loadingServices ? (
                  <p className="text-sm text-slate-400">Loading services...</p>
                ) : services.length === 0 ? (
                  <div className="text-center py-6 bg-slate-50 rounded-xl">
                    <p className="text-slate-400 text-sm">No pending defective parts found</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {services.map(service => (
                      <div key={service.id} className="border border-slate-200 rounded-xl overflow-hidden">
                        {/* Service header */}
                        <div className="bg-slate-50 px-3 py-2 border-b border-slate-100">
                          <p className="text-xs font-semibold text-slate-700">
                            {service.complaint || "Service"}
                          </p>
                          <p className="text-xs text-slate-400">
                            {service.client_name} → {service.site_name}
                          </p>
                        </div>
                        {/* Defective items */}
                        {(service.defective_items || [])
                          .map((item, idx) => ({ item, idx }))
                          .filter(({ item }) => !item.oem_return_status || item.oem_return_status === "pending")
                          .map(({ item, idx }) => {
                            const key = `${service.id}_${idx}`;
                            const selected = selectedParts.some(p => p.key === key);
                            return (
                              <div
                                key={idx}
                                onClick={() => togglePart(service.id, idx, item)}
                                className={`px-3 py-2.5 cursor-pointer transition flex items-start gap-3 ${
                                  selected ? "bg-red-50 border-l-4 border-red-400" : "hover:bg-slate-50"
                                }`}>
                                <div className={`mt-0.5 w-4 h-4 rounded border-2 flex-shrink-0 flex items-center justify-center ${
                                  selected ? "bg-red-500 border-red-500" : "border-slate-300"
                                }`}>
                                  {selected && <span className="text-white text-[10px] font-bold">✓</span>}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-semibold text-slate-700">{item.item_name}</p>
                                  <p className="text-xs text-slate-500">
                                    {item.machine_description && `${item.machine_description} • `}
                                    S/N: {item.serial_no || "—"}
                                    {item.part_code && ` • Code: ${item.part_code}`}
                                  </p>
                                  <p className="text-xs text-slate-400">Qty: {item.qty}</p>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Selection summary */}
              {selectedParts.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                  <p className="text-xs font-semibold text-red-700">
                    {selectedParts.length} part(s) selected for OEM return
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-5 border-t flex gap-3">
              <button onClick={() => setShowCreate(false)}
                className="flex-1 py-3 rounded-xl bg-slate-100 text-slate-600 font-semibold text-sm">
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={saving || !selectedParts.length}
                className="flex-1 py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold text-sm transition disabled:opacity-40">
                {saving ? "Creating..." : "Create OEM Return"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}